import type { Transaction } from './types';

const MAX_UNDO_DEPTH = 50;

export interface CommandHistory {
  pushTransaction(tx: Transaction): void;
  undo(): Transaction | null;
  redo(): Transaction | null;
  canUndo(): boolean;
  canRedo(): boolean;
  clear(): void;
  getHistory(): readonly Transaction[];
  subscribe(listener: () => void): () => void;
}

export function createCommandHistory(): CommandHistory {
  const undoStack: Transaction[] = [];
  const redoStack: Transaction[] = [];
  const listeners = new Set<() => void>();

  function notify() {
    for (const fn of listeners) fn();
  }

  return {
    pushTransaction(tx: Transaction) {
      undoStack.push(tx);
      if (undoStack.length > MAX_UNDO_DEPTH) {
        undoStack.shift();
      }
      redoStack.length = 0;
      notify();
    },

    undo(): Transaction | null {
      const tx = undoStack.pop();
      if (!tx) return null;
      redoStack.push(tx);
      notify();
      return tx;
    },

    redo(): Transaction | null {
      const tx = redoStack.pop();
      if (!tx) return null;
      undoStack.push(tx);
      notify();
      return tx;
    },

    canUndo() {
      return undoStack.length > 0;
    },

    canRedo() {
      return redoStack.length > 0;
    },

    clear() {
      undoStack.length = 0;
      redoStack.length = 0;
      notify();
    },

    getHistory() {
      return undoStack;
    },

    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
  };
}
